/** History: pick a new broker/account for a trade or cash row, with confirm. */

import { isIrrelevantScopeAccount } from "./accountScope";
import { choiceDialog, confirmDialog } from "./confirmDialog";
import {
  markForAccountId,
  ensureLedgerMarkIndex,
  invalidateLedgerMarkIndex,
  type LedgerDisplayMark,
} from "./ledgerMarks";
import {
  loadLedgerCatalog,
  reassignOwnLedgerCashFlowAccount,
  reassignOwnLedgerTransactionAccount,
  type LedgerAccount,
} from "./ownLedgerClient";
import { showToast } from "./toast";

export type HistoryAccountReassignTarget =
  | { kind: "transaction"; id: number; currentAccountId?: string | null }
  | { kind: "cash"; id: number; currentAccountId?: string | null };

function accountChoiceLabel(account: LedgerAccount, mark: LedgerDisplayMark | null): string {
  const broker = mark?.brokerCode || account.broker_name || account.broker_id || "Broker";
  const short = mark?.accountShort || account.short_name || account.label || account.account_id;
  return `${broker} · ${short}`;
}

function accountChoiceHint(account: LedgerAccount, mark: LedgerDisplayMark | null): string {
  const broker = mark?.brokerTitle || account.broker_name || account.broker_id || "";
  const name = mark?.accountTitle || account.label || account.name || account.account_id;
  return [broker, name].filter(Boolean).join(" · ");
}

async function loadAssignableAccounts(): Promise<LedgerAccount[]> {
  await ensureLedgerMarkIndex(true);
  const catalog = await loadLedgerCatalog(true);
  return catalog.accounts
    .filter((account) => !isIrrelevantScopeAccount(account))
    .sort((a, b) => {
      const aMark = markForAccountId(a.account_id);
      const bMark = markForAccountId(b.account_id);
      const aLabel = accountChoiceLabel(a, aMark);
      const bLabel = accountChoiceLabel(b, bMark);
      return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
    });
}

/**
 * Choice list → confirm → API. Returns true when the ledger changed.
 */
export async function promptHistoryAccountReassign(
  target: HistoryAccountReassignTarget,
): Promise<boolean> {
  const currentId = String(target.currentAccountId ?? "").trim();
  let accounts: LedgerAccount[];
  try {
    accounts = await loadAssignableAccounts();
  } catch (err) {
    showToast(err instanceof Error ? err.message : "Could not load accounts.", "error");
    return false;
  }
  if (!accounts.length) {
    showToast("No ledger accounts available.", "error");
    return false;
  }

  const currentAccount = currentId
    ? accounts.find((account) => account.account_id === currentId)
    : undefined;
  const currentMark = currentId ? markForAccountId(currentId) : null;
  const currentLabel = currentAccount
    ? accountChoiceLabel(currentAccount, currentMark)
    : currentId || "Unassigned";

  const choices = accounts.filter((account) => account.account_id !== currentId);
  if (!choices.length) {
    showToast(`Already on ${currentLabel} — no other accounts to switch to.`, "info");
    return false;
  }

  const selectedId = await choiceDialog({
    title: "Change broker / account",
    message: `Currently ${currentLabel}. Pick the account this ${target.kind === "cash" ? "cash row" : "trade"} should belong to.`,
    options: choices.map((account) => {
      const mark = markForAccountId(account.account_id);
      return {
        id: account.account_id,
        label: accountChoiceLabel(account, mark),
        hint: accountChoiceHint(account, mark),
      };
    }),
  });
  if (!selectedId || selectedId === currentId) return false;

  const next = accounts.find((account) => account.account_id === selectedId);
  if (!next) return false;
  const nextMark = markForAccountId(next.account_id);
  const nextLabel = accountChoiceLabel(next, nextMark);
  const linkedNote =
    target.kind === "transaction"
      ? " Assignment-linked legs (option ASGN and its stock leg) move with it."
      : "";

  const confirmed = await confirmDialog({
    title: "Confirm account change",
    message: `Move this ${target.kind === "cash" ? "cash row" : "trade"} from ${currentLabel} to ${nextLabel}?${linkedNote}`,
    confirmLabel: "Change account",
  });
  if (!confirmed) return false;

  try {
    if (target.kind === "cash") {
      await reassignOwnLedgerCashFlowAccount(target.id, selectedId);
    } else {
      await reassignOwnLedgerTransactionAccount(target.id, selectedId);
    }
    invalidateLedgerMarkIndex();
    await ensureLedgerMarkIndex(true);
    showToast(`Moved to ${nextLabel}.`, "success");
    return true;
  } catch (err) {
    showToast(err instanceof Error ? err.message : "Could not change account.", "error");
    return false;
  }
}
